// src/screens/AdminDashboard.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase/config';
import {
  FaHome,
  FaUser,
  FaClipboardList,
  FaQuestionCircle,
  FaSignOutAlt,
  FaFlag,
  FaShieldAlt,
  FaUserCog
} from 'react-icons/fa';
import logo from '../assets/logo.png';

const APP_PRIMARY = '#b03060';

const AdminDashboard = () => {
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      alert('Error al cerrar sesión: ' + error.message);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.mainContent}>
        <div style={adminStyles.wrapper}>
          {/* Cabecera con Logo */}
          <div style={adminStyles.brandHeader}>
            <img src={logo} alt="Mariana Fitness Trainer" style={adminStyles.logo} />
          </div>

          {/* Tarjeta de Bienvenida */}
          <div style={adminStyles.heroCard}>
            <div style={adminStyles.avatarContainer}>
              <div style={adminStyles.avatarWrapper}>
                <FaUserCog style={adminStyles.avatarFallbackIcon} />
                <div style={adminStyles.badgeIcon}>
                  <FaShieldAlt size={13} color="#ffffff" />
                </div>
              </div>
            </div>

            <div style={adminStyles.greetingContainer}>
              <span style={adminStyles.welcomeBadge}>Panel de Administración</span>
              <h1 style={adminStyles.greetingText}>¡Hola, Mariana!</h1>
              <p style={adminStyles.subText}>
                Control general de usuarios, asignación de planes y reportes de la plataforma
              </p>
            </div>
          </div>

          {/* Cuadrícula de 2 columnas: Fila 1 (Usuarios y Rutinas), Fila 2 (FAQ y Reportes) */}
          <div style={adminStyles.quickGrid}>
            <button
              type="button"
              onClick={() => navigate('/admin/usuarios')}
              style={adminStyles.quickCard}
            >
              <div style={{ ...adminStyles.quickIconBox, background: '#f0fdf4' }}>
                <FaUser color="#16a34a" size={20} />
              </div>
              <div style={adminStyles.quickInfo}>
                <strong style={adminStyles.quickTitle}>Usuarios</strong>
                <span style={adminStyles.quickDesc}>Gestionar alumnos registrados</span>
              </div>
            </button>

            <button
              type="button"
              onClick={() => navigate('/admin/rutinas')}
              style={adminStyles.quickCard}
            >
              <div style={{ ...adminStyles.quickIconBox, background: '#fdf2f8' }}>
                <FaClipboardList color={APP_PRIMARY} size={20} />
              </div>
              <div style={adminStyles.quickInfo}>
                <strong style={adminStyles.quickTitle}>Rutinas y Videos</strong>
                <span style={adminStyles.quickDesc}>Organizar y asignar ejercicios</span>
              </div>
            </button>

            <button
              type="button"
              onClick={() => navigate('/admin/faq')}
              style={adminStyles.quickCard}
            >
              <div style={{ ...adminStyles.quickIconBox, background: '#eff6ff' }}>
                <FaQuestionCircle color="#2563eb" size={20} />
              </div>
              <div style={adminStyles.quickInfo}>
                <strong style={adminStyles.quickTitle}>Preguntas Frecuentes</strong>
                <span style={adminStyles.quickDesc}>Editar respuestas de ayuda</span>
              </div>
            </button>

            <button
              type="button"
              onClick={() => navigate('/admin/reportes')}
              style={adminStyles.quickCard}
            >
              <div style={{ ...adminStyles.quickIconBox, background: '#fffbeb' }}>
                <FaFlag color="#d97706" size={20} />
              </div>
              <div style={adminStyles.quickInfo}>
                <strong style={adminStyles.quickTitle}>Reportes</strong>
                <span style={adminStyles.quickDesc}>Estadísticas de visualización</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Dock inferior limpio: Solo Inicio y Cerrar Sesión */}
      <div style={navDockStyles.dockContainer}>
        <div style={navDockStyles.dockPill}>
          <button
            type="button"
            onClick={() => navigate('/admin')}
            style={{
              ...navDockStyles.dockButton,
              backgroundColor: '#fdf2f8',
              color: APP_PRIMARY,
              fontWeight: '700'
            }}
          >
            <div
              style={{
                ...navDockStyles.iconWrapper,
                backgroundColor: APP_PRIMARY,
                color: '#ffffff'
              }}
            >
              <FaHome size={15} />
            </div>
            <span>Inicio</span>
          </button>

          <div style={navDockStyles.divider} />

          <button
            type="button"
            onClick={handleLogout}
            style={{
              ...navDockStyles.dockButton,
              color: '#dc2626'
            }}
          >
            <div
              style={{
                ...navDockStyles.iconWrapper,
                backgroundColor: '#fee2e2',
                color: '#dc2626'
              }}
            >
              <FaSignOutAlt size={14} />
            </div>
            <span>Cerrar sesión</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;

/* ------------------------------ ESTILOS ------------------------------ */
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5'
  },
  mainContent: {
    flex: 1,
    padding: 20,
    paddingBottom: 110
  }
};

const adminStyles = {
  wrapper: {
    maxWidth: 780,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 20
  },
  brandHeader: {
    display: 'flex',
    justifyContent: 'center',
    padding: '8px 0 2px 0'
  },
  logo: {
    height: 72,
    width: 'auto',
    objectFit: 'contain'
  },
  heroCard: {
    background: '#ffffff',
    borderRadius: 22,
    padding: '36px 24px 28px 24px',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.05)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    border: '1px solid #f1f5f9'
  },
  avatarContainer: {
    position: 'relative',
    marginBottom: 16
  },
  avatarWrapper: {
    width: 110,
    height: 110,
    borderRadius: '50%',
    padding: 4,
    background: `linear-gradient(135deg, ${APP_PRIMARY}, #f472b6)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    boxShadow: '0 8px 20px rgba(176, 48, 96, 0.22)'
  },
  avatarFallbackIcon: {
    fontSize: 48,
    color: '#ffffff'
  },
  badgeIcon: {
    position: 'absolute',
    bottom: 2,
    right: 2,
    width: 30,
    height: 30,
    borderRadius: '50%',
    backgroundColor: APP_PRIMARY,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2.5px solid #ffffff',
    boxShadow: '0 2px 6px rgba(0,0,0,0.18)'
  },
  greetingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8
  },
  welcomeBadge: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: APP_PRIMARY,
    background: '#fdf2f8',
    padding: '4px 12px',
    borderRadius: 20
  },
  greetingText: {
    fontSize: 26,
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
    letterSpacing: '-0.3px',
    fontFamily: "'Poppins', sans-serif"
  },
  subText: {
    fontSize: 14,
    color: '#64748b',
    margin: 0,
    maxWidth: 520,
    lineHeight: 1.5
  },
  quickGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 16
  },
  quickCard: {
    background: '#ffffff',
    border: '1px solid #f1f5f9',
    borderRadius: 16,
    padding: '18px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.03)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    textAlign: 'left'
  },
  quickIconBox: {
    width: 46,
    height: 46,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  quickInfo: {
    display: 'flex',
    flexDirection: 'column'
  },
  quickTitle: {
    fontSize: 15,
    color: '#1e293b',
    fontWeight: '600'
  },
  quickDesc: {
    fontSize: 12,
    color: '#94a3b8',
    marginTop: 2
  }
};

const navDockStyles = {
  dockContainer: {
    position: 'fixed',
    bottom: 20,
    left: 0,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    pointerEvents: 'none',
    padding: '0 16px',
    boxSizing: 'border-box'
  },
  dockPill: {
    pointerEvents: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.94)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: '1.5px solid rgba(241, 245, 249, 0.95)',
    borderRadius: 50,
    padding: '6px 12px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease'
  },
  dockButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: 'none',
    borderRadius: 50,
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '13px',
    fontFamily: "'Poppins', sans-serif",
    transition: 'all 0.2s ease',
    outline: 'none',
    backgroundColor: 'transparent',
    color: '#64748b',
    fontWeight: '600'
  },
  iconWrapper: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    backgroundColor: '#f1f5f9',
    color: '#64748b'
  },
  divider: {
    width: 1,
    height: 22,
    backgroundColor: '#e2e8f0',
    margin: '0 2px'
  }
};